import useContent from './useContent';
import { defineStore, acceptHMRUpdate } from 'pinia';
import useSuspend from '../lib/useSuspend';
import { watch, nextTick, reactive, computed, ref } from 'vue';
import SCORM, {
  passed,
  failed as scormFailed,
  setScore,
  setCompleted,
  set,
  get
} from '../lib/SCORM';
import submitOmnitrackIntegrations from '../lib/submitOmnitrackIntegrations';
import { submitInsightsData } from '../lib/insightsData';
import useBadges from './useBadges';
import { isHiddenInHierarchy } from '../lib/isHidden';

const useQuizzes = defineStore('quizzes', () => {
  // maps quiz ID to Set of question IDs
  const quizMap = reactive(new Map<string, Set<string>>());
  // store status for each quiz
  const statuses: Record<string, QuizStatus> = reactive({});

  const averageQuizAttempts = reactive({ value: 0 });

  const unitMap = reactive(new Map<string, Set<string>>());

  const suspend = useSuspend() as Suspend;
  const content = useContent().compiled;

  // Persisted quiz results (correct/total/attempts/exceededAttempts) from SCORM suspend data.
  // Used as a fallback during resume while questions are still registering.
  const quizResults = reactive<
    Record<
      string,
      {
        correct?: number;
        total?: number;
        attempts?: number;
        exceededAttempts?: boolean;
      }
    >
  >({});

  // Load persisted quiz results from SCORM suspend data
  if (suspend?.quizResults) {
    Object.entries(suspend.quizResults).forEach(([quizId, result]) => {
      quizResults[quizId] = result as {
        correct?: number;
        total?: number;
        attempts?: number;
        exceededAttempts?: boolean;
      };
    });
  }

  // Backwards compatibility: migrate deprecated suspend.quizAttempts into quizResults
  if (suspend?.quizAttempts) {
    Object.entries(suspend.quizAttempts).forEach(([quizId, attempts]) => {
      if (!quizResults[quizId]) {
        quizResults[quizId] = {};
      }
      quizResults[quizId].attempts = attempts as number;
      // Persist the migrated value under the new key
      set(`quizResults.${quizId}.attempts`, attempts as number);
    });
    // Remove the deprecated property from suspend data
    set('quizAttempts', null);
    console.log('Migrated suspend.quizAttempts → quizResults.*.attempts');
  }

  const unlockedForEditMode = ref(false);

  const getQuiz = (quizId: string) =>
    content?.quizzes?.find(q => q.id === quizId);

  const getQuizUnits = (quizId: string) => {
    return unitMap.get(quizId);
  };

  const computeQuizStatus = (quizId: string): QuizStatus => {
    let submitted = 0;
    let correct = 0;
    let total = 0;
    // defer call to useBadges until needed
    const { getPageBadge } = useBadges();
    const questions = quizMap.get(quizId);
    // check badges if no content quizzes passmark. For minimal badge YAML page ID is quiz ID
    const passmark =
      getQuiz && getQuiz(quizId)?.passmark
        ? getQuiz(quizId)?.passmark
        : getPageBadge && getPageBadge(quizId)?.passmark
          ? getPageBadge(quizId).passmark
          : 100;

    questions?.forEach(questionId => {
      total++;
      correct +=
        suspend?.completed[questionId] && suspend?.correctQuestions[questionId]
          ? 1
          : 0;
      submitted += suspend?.completed[questionId] ? 1 : 0;
    });

    // On resume, questions register one-by-one so total grows gradually.
    // Each intermediate state can look "completed" with a wrong score.
    // If we have persisted results from the last completed attempt and
    // the current total is less (not all questions registered yet),
    // use the persisted correct/total for an accurate score.
    const persistedResult = quizResults[quizId];
    if (
      persistedResult?.total &&
      persistedResult?.correct != null &&
      total > 0 &&
      total < persistedResult.total
    ) {
      correct = persistedResult.correct;
      total = persistedResult.total;
      submitted = total;
    }

    const score = total > 0 ? Math.round((correct / total) * 100) : 0;
    const completed = submitted === total;
    const failed = completed && passmark > 0 && score < passmark;
    const passed = completed && passmark > 0 && score >= passmark;
    const attempts = persistedResult?.attempts || 0;
    const scored = isQuizScored(quizId);
    const achievable = isPassmarkAchievable(
      total,
      submitted,
      passmark,
      correct
    );

    // Carry forward the persisted exceededAttempts flag
    const exceededAttempts = persistedResult?.exceededAttempts || false;

    return {
      id: quizId,
      score,
      scored,
      completed,
      passed,
      failed,
      submitted,
      correct,
      achievable,
      attempts,
      exceededAttempts,
      total,
      passmark
    };
  };

  /**
   * Updates quiz status and increments attempts count only when
   * the quiz transitions to completed state.
   */
  const updateQuizStatus = (quizId: string) => {
    const previousStatus = statuses[quizId];
    const newStatus = computeQuizStatus(quizId);

    // Increment attempts only when quiz transitions to completed state
    if (newStatus.completed && !previousStatus?.completed) {
      // Only increment if previousStatus exists — this means the quiz genuinely
      // transitioned during the session. When previousStatus is undefined it's
      // the initial computation on registration (e.g. resume from suspend data)
      // and the persisted attempt count is already correct.
      if (previousStatus !== undefined) {
        const prevAttempts = quizResults[quizId]?.attempts || 0;
        const newAttempts = prevAttempts + 1;

        // Persist attempts, correct, and total together in quizResults
        if (!quizResults[quizId]) {
          quizResults[quizId] = {};
        }
        quizResults[quizId].attempts = newAttempts;
        quizResults[quizId].correct = newStatus.correct;
        quizResults[quizId].total = newStatus.total;
        quizResults[quizId].exceededAttempts = newStatus.exceededAttempts;

        set(`quizResults.${quizId}.attempts`, newAttempts);
        set(`quizResults.${quizId}.correct`, newStatus.correct);
        set(`quizResults.${quizId}.total`, newStatus.total);
      }
      newStatus.attempts = quizResults[quizId]?.attempts || 0;
    }

    statuses[quizId] = newStatus;
    return newStatus;
  };

  const setAnyTags = (quizId: string) => {
    const def = getQuiz(quizId);

    if (!def) return;

    try {
      const { failed, passed } = statuses[quizId];

      const tagsString =
        (failed && def['if-failed']) || (passed && def['if-passed']);

      if (tagsString) {
        const tags = {};

        tagsString.split(/\s+/).forEach(t => {
          tags[t] = true;
        });

        SCORM.setTags(tags);
      }
    } catch (e) {
      console.warn(e);
    }
  };

  // calculate completed, correct from suspend for all questions.
  const computeQuizQuestionStatuses = computed(() => {
    const quizQuestionStatuses = new Map<string, string[]>();

    // getQuiz to match registered questions
    quizMap.forEach((quiz, quizId) => {
      const questionMap = new Array();
      quiz.forEach(questionId => {
        const completed = suspend?.completed[questionId] ? true : false;
        const correct =
          completed && suspend?.correctQuestions[questionId] ? true : false;
        questionMap.push({
          id: questionId,
          completed: completed,
          correct: correct
        });
      });
      quizQuestionStatuses.set(quizId, questionMap);
    });

    return quizQuestionStatuses;
  });

  function register(
    question: ScreenQuestion,
    pageId: object
  ): Ref<Quiz | undefined> {
    // TODO - if a question is in a hidden bank or quiz it should also be excluded
    if (isHiddenInHierarchy(question.id, true)) {
      console.log(
        `Quiz question is hidden somewhere in its hierarchy: ${question.id}`
      );
      return ref();
    }

    // defer call to useBadges until needed
    const { getPageBadge } = useBadges();
    const quizId = computed(() => {
      // TODO - do we need to validate for duplicate quiz ID's
      // TODO - check if question is in an assessment template, they should not be added if no for-quiz
      if (question['for-quiz']) {
        return question['for-quiz'];
      } else if (pageId && getPageBadge && getPageBadge(pageId.value)) {
        return pageId.value;
      } else {
        return undefined;
      }
    });
    const questionId = computed(() => question.id);

    if (typeof quizId.value !== 'string') {
      return ref();
    }

    const updateMap = () => {
      if (!quizId.value) return;

      // make sure the quiz is in the quizMap
      if (!quizMap.has(quizId.value)) {
        quizMap.set(quizId.value, reactive(new Set()));
      }

      // add question to the quiz
      quizMap.get(quizId.value)?.add(questionId.value);
      updateQuizStatus(quizId.value);

      // add pageId to unitMap
      if (!unitMap.has(quizId.value)) {
        unitMap.set(quizId.value, new Set());
      }
      unitMap.get(quizId.value)?.add(pageId.value);
    };

    updateMap();

    watch([quizId, questionId], (_, [prevQuizId, prevQuestionId]) => {
      if (!prevQuizId) return;
      const quizSet = quizMap.get(prevQuizId);
      quizSet?.delete(prevQuestionId);
      updateMap();
    });

    const quizDef = computed(() =>
      useContent().compiled?.quizzes?.find(q => q.id === quizId.value)
    );

    watch(
      [quizDef, () => suspend?.completed[questionId.value]],
      async () => {
        await nextTick();

        if (quizId.value) {
          updateQuizStatus(quizId.value);
          setAnyTags(quizId.value);
        }
      },
      { deep: true }
    );

    console.log(
      `Registering quiz for page: ${pageId.value} with quiz: ${
        quizId.value
      } and questions: ${Array.from(quizMap.get(quizId.value) || []).join(
        ', '
      )}`
    );

    return quizDef;
  }

  // watch(isEditModeActive, (curr, prev) => {
  //   if (curr !== prev) {
  //     setAllQuizzesForEditMode(isEditModeActive.value);
  //   }
  // });

  const isAdaptiveCourse = computed(() => {
    if (!content?.quizzes) return false;

    // iterate over quizzes to find those used for tagging (pre-assessment)
    for (const quiz of content?.quizzes) {
      if (quiz['if-failed'] || quiz['if-passed']) {
        return true;
      }
    }

    return false;
  });

  // get all 'pre-assessment' quizzes based on the assumption that pre-assessment quizzes are in a screen using the quiz-progress template with an 'includes' prop
  function allAdaptiveQuizzes() {
    const quizzes = [];

    if (isAdaptiveCourse.value) {
      for (const quiz of content?.quizzes) {
        if (quiz['if-failed'] || quiz['if-passed']) {
          quizzes.push(quiz);
        }
      }
    }

    return quizzes;
  }

  const findQuizScreenId = quizId => {
    for (const page of content?.pages) {
      for (const screen of page.screens) {
        if (screen.template === 'quiz' && screen?.from === quizId) {
          return screen.id;
        }
      }
    }
    return null;
  };

  const hasScoredResultScreen = (quizId: string): boolean => {
    for (const page of content?.pages) {
      for (const screen of page.screens) {
        if (
          screen.template === 'result' &&
          screen?.['for-quiz'] === quizId &&
          screen.scored
        ) {
          return true;
        }
      }
    }
    return false;
  };

  // when we switch to edit mode in an adaptive course, we need to set all pre-assessment quizzes to failed to show all the tagged content (passing a quiz will usually hide tagged content)
  function setAllQuizzesForEditMode(editModeActive) {
    const adaptiveQuizzes = allAdaptiveQuizzes();

    if (adaptiveQuizzes.length === 0 || !editModeActive) return;

    const failStatus = {
      score: 0,
      scored: false,
      completed: true,
      passed: false,
      failed: true,
      submitted: 1,
      correct: 0,
      achievable: false,
      attempts: 1,
      total: 1,
      passmark: 100
    };

    console.log({
      editModeActive,
      'adaptive course': isAdaptiveCourse.value,
      'adaptive quizzes': adaptiveQuizzes.length
    });

    // fail all the adaptive quiz questions to show all the tagged content
    adaptiveQuizzes.forEach(quiz => {
      const questions = quiz?.questions;
      if (!questions) return;

      setCompleted(quiz?.id);

      questions.forEach((question, i) => {
        setCompleted(`${quiz?.id}-section${i}`);
        setCompleted(question.id);
        // set(`correctQuestions.${question.id}`, false);

        if (suspend?.correctQuestions)
          delete suspend.correctQuestions[question.id];
      });

      statuses[quiz?.id] = failStatus;

      // complete the quiz's associated screen
      const quizScreenId = findQuizScreenId(quiz?.id);
      if (quizScreenId) setCompleted(quizScreenId);

      // log the failed status and questions
      console.log('failed quizzes', {
        id: quiz?.id,
        status: failStatus,
        questions: questions
      });
    });

    // store a ref so we only do this once
    unlockedForEditMode.value = true;
  }

  function isPassmarkAchievable(total, completed, passmark, correct) {
    // Calculate the minimum number of correct answers needed to achieve the passmark
    const minCorrect = Math.ceil((passmark / 100) * total);

    // Calculate the remaining questions that need to be answered
    const remainingQuestions = total - completed;

    // Calculate the remaining correct answers needed to achieve the passmark
    const remainingCorrect = minCorrect - correct;

    // Check if the passmark is achievable
    return remainingCorrect <= remainingQuestions;
  }

  // check if the quiz is scored or if a result screen with matching "for-quiz" is scored
  function isQuizScored(quizId: string): boolean {
    return getQuiz(quizId)?.scored || hasScoredResultScreen(quizId);
  }

  /**
   * Computes a score based on specified, completed quiz IDs, or all scored & completed quizzes. Includes any completed assessment score.
   * We specify quizIDs when we want to only use certain quizzes for the scoring. This is used in adaptive courses to calculate a score based on the not-scored adaptive content quizzes if learners test out of the adaptive course content including the final quiz/assessment.
   */
  function computeScore(quizIds?: string[] | undefined) {
    let correct = 0;
    let total = 0;

    // filter the quizzes to use for calculating the final course score:
    // - if we have specified quiz IDs, use only those that are completed
    // - if we don't specify quiz IDs, use all scored, completed quizzes
    const filteredQuizStatuses = quizIds
      ? Object.values(statuses).filter(
          status => quizIds.includes(status.id) && status.completed
        )
      : Object.values(statuses).filter(
          status => status.scored && status.completed
        );

    filteredQuizStatuses?.forEach(quizStatus => {
      const { correct: c, total: t } = quizStatus;
      correct += c || 0;
      total += t || 0;
      console.log(
        'compute quiz score',
        quizStatus.id,
        `${c}/${t}`,
        `passmark: ${quizStatus.passmark}`,
        `passed: ${quizStatus.passed}`,
        `attempts: ${quizStatus.attempts}`
      );
    });

    // if we don't specify quiz IDs, use/include the assessment score if it is completed
    if (!quizIds && suspend?.assessment?.completed) {
      const { correct: c, total: t } = suspend.assessment;
      correct += c || 0;
      total += t || 0;
      console.log('compute assessment score', `${c}/${t}`);
    }

    const score = total > 0 ? Math.round((correct / total) * 100) : 0;
    console.log('computed score', `${correct}/${total} = ${score}%`);

    // save the first assessment/quiz attempt score separately to insights data
    if (!get(`insightsData.metadata.assessmentFirstScore`)) {
      set(`insightsData.metadata.assessmentFirstScore`, score);
    }

    return score;
  }

  function computeAverageQuizAttempts(quizIds?: string[] | undefined): number {
    // filter the quizzes to use for calculating the average quiz attempts:
    // - if we have specified quiz IDs, use only those that are completed
    // - if we don't specify quiz IDs, use all scored, completed quizzes
    const filteredQuizStatuses = quizIds
      ? Object.values(statuses).filter(
          status => quizIds.includes(status.id) && status.completed
        )
      : Object.values(statuses).filter(
          status => status.scored && status.completed
        );

    if (filteredQuizStatuses.length === 0) return 0;

    const averageAttempts =
      filteredQuizStatuses.reduce(
        (acc, quiz) => acc + (quiz?.attempts || 0),
        0
      ) / filteredQuizStatuses.length;

    // limit to 2 decimal places
    return Math.round(averageAttempts * 100) / 100;
  }

  /**
   * Marks a quiz as having exceeded its max attempts and persists the flag.
   * Called by screen-result.vue when the exceeded-attempts condition triggers.
   */
  function setQuizExceededAttempts(quizId: string) {
    if (statuses[quizId]) {
      statuses[quizId].exceededAttempts = true;
    }
    // Persist the flag so it survives resume
    if (!quizResults[quizId]) {
      quizResults[quizId] = {
        correct: statuses[quizId]?.correct,
        total: statuses[quizId]?.total,
        attempts: statuses[quizId]?.attempts,
        exceededAttempts: true
      };
    } else {
      quizResults[quizId].exceededAttempts = true;
    }
    set(`quizResults.${quizId}.exceededAttempts`, true);
  }

  /**
   * Fails the course via SCORM when max quiz attempts have been exceeded.
   * Computes and sets the final score before marking the course as failed.
   */
  function failCourse(passCourse?: number | string | undefined) {
    const score =
      passCourse && typeof passCourse === 'string'
        ? computeScore(passCourse.split(/\s+/))
        : computeScore();

    setScore(score);
    console.log(`Course failed due to exceeded quiz attempts. Score: ${score}`);
    nextTick().then(() => scormFailed());
  }

  // accept a forced score when pass-course: {number} is set
  function completeCourse(passCourse?: number | string | undefined) {
    let score = 0;
    if (passCourse && typeof passCourse === 'number') {
      score = passCourse;
      averageQuizAttempts.value = 1;
      console.log(`Setting score with FIXED NUMBER passCourse: ${passCourse}`);
    } else if (passCourse && typeof passCourse === 'string') {
      score = computeScore(passCourse.split(/\s+/));
      averageQuizAttempts.value = computeAverageQuizAttempts(
        passCourse.split(/\s+/)
      );
      console.log(`Calculating score from quizIDs: ${passCourse}`);
    } else {
      score = computeScore();
      averageQuizAttempts.value = computeAverageQuizAttempts();
      console.log(
        `Calculating score from all scored quizzes and/or assessment`
      );
    }

    setScore(score);

    console.log(`Completing course with score: ${score}`);

    // wait for the next tick to ensure the score is sent first
    nextTick().then(() => passed());

    submitOmnitrackIntegrations();
    submitInsightsData();
  }

  watch(
    () => suspend?.assessment?.completed,
    () => console.log('assessment completed', computeScore())
  );

  return {
    register,
    computeQuizStatus,
    completeCourse,
    statuses,
    getQuiz,
    computeQuizQuestionStatuses,
    getQuizUnits,
    unitMap,
    isAdaptiveCourse,
    setAllQuizzesForEditMode,
    unlockedForEditMode,
    averageQuizAttempts,
    quizResults,
    setQuizExceededAttempts,
    failCourse
  };
});

export default useQuizzes;

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useQuizzes, import.meta.hot));
}
